import React from "react";
import { ArrowUpRight } from "lucide-react";

import { verifyLucimixTicket } from "../../api/client";
import { requestTicket } from "./lucimixHandshake";
import styles from "./authBoundary.module.css";


// 按后端 error.code（或握手阶段的 no_ticket）给用户看得懂的话，不把内部错误原样吐出来。
const MESSAGES = {
  no_ticket: "没有收到 Lucimix 的登录票据，请从 Lucimix 里重新打开本应用。",
  lucimix_ticket_invalid: "登录票据已失效，请从 Lucimix 里重新打开本应用。",
  lucimix_unavailable: "Lucimix 身份服务暂时不可用，请稍后重试。",
  lucimix_not_configured: "Lucimix 入口暂未启用。",
  account_disabled: "该账号已被停用。",
  session_not_kept:
    "已通过 Lucimix 验证，但浏览器没有保存登录状态（可能阻止了嵌入页面的 Cookie）。请联系管理员。",
};
const FALLBACK_MESSAGE = "暂时无法通过 Lucimix 登录，请稍后重试。";

// 票据是一次性的、也是稀缺的：一次握手只该发一轮 ready、只该拿一张票据。
// React 严格模式（开发环境）会把 effect 跑两遍，各自发 ready 就会浪费一张；
// 所以在握手结束之前，所有调用方共享同一个 Promise。结束（成功/失败）后清掉，
// 「重新连接」和组件重新挂载都会开一轮全新的握手。
let pendingHandshake = null;
function sharedRequestTicket() {
  if (!pendingHandshake) {
    const handshake = requestTicket();
    pendingHandshake = handshake;
    const clear = () => {
      if (pendingHandshake === handshake) pendingHandshake = null;
    };
    handshake.then(clear, clear);
  }
  return pendingHandshake;
}


/**
 * 嵌在 Lucimix iframe 里时代替登录页：自动握手换票据 → 后端验证 → 进入应用，
 * 用户全程看不到用户名/密码表单。失败时给出原因和「重新连接」（重新握手会拿到新票据）。
 * `blockedReason` 由 AuthBoundary 传入：登录后 Cookie 存不住时不再自动重试。
 */
export function LucimixEntry({ onSignedIn, blockedReason = null }) {
  const [attempt, setAttempt] = React.useState(0);
  const [failure, setFailure] = React.useState(null);

  React.useEffect(() => {
    if (blockedReason) return undefined;
    const controller = new AbortController();
    setFailure(null);
    void (async () => {
      try {
        const ticket = await sharedRequestTicket();
        // 被取消的那一轮（严格模式的第一遍 effect）不能再拿这张一次性票据去验证，
        // 否则和存活的那一轮各验一次，第二次必然 401。
        if (controller.signal.aborted) return;
        const payload = await verifyLucimixTicket(ticket);
        if (!controller.signal.aborted) onSignedIn(payload);
      } catch (caught) {
        if (controller.signal.aborted) return;
        setFailure(caught?.code === "no_ticket" ? "no_ticket" : caught?.payload?.error?.code ?? "unknown");
      }
    })();
    return () => controller.abort();
  }, [attempt, blockedReason, onSignedIn]);

  const code = blockedReason ?? failure;
  if (!code) {
    return (
      <main className={styles.authPage}>
        <p className={styles.authChecking} role="status">正在通过 Lucimix 登录…</p>
      </main>
    );
  }
  return (
    <main className={styles.authPage} id="main-content">
      <section className={styles.loginCard} aria-labelledby="lucimix-entry-title">
        <div className={styles.loginBrand}>
          <span className={styles.loginBrandMark}><ArrowUpRight size={24} aria-hidden="true" /></span>
          <span>Astock Agent</span>
        </div>
        <h1 id="lucimix-entry-title">无法通过 Lucimix 登录</h1>
        <div className={styles.loginForm}>
          <p className={styles.loginError} role="alert">{MESSAGES[code] ?? FALLBACK_MESSAGE}</p>
          {!blockedReason && (
            <button className={styles.loginButton} type="button" onClick={() => setAttempt((n) => n + 1)}>
              重新连接
            </button>
          )}
        </div>
      </section>
    </main>
  );
}
